import { useRef, useState } from "react";
import { Plus, Pencil, CheckSquare, Mic, Image as ImageIcon, StickyNote, X, FileText, Camera, Video } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { uploadImage, uploadDocument } from "@/lib/api";
import DrawingPad from "@/components/DrawingPad";
import AudioRecorder from "@/components/AudioRecorder";

/**
 * Mobile-only Floating Action Button. On phones the top QuickCreate bar is
 * still visible, but a quick-reach FAB in the bottom-right offers one-tap
 * entry into Note, Checklist, Drawing, Audio and Image flows.
 *
 * `onCreate(payload)` is called with the exact note shape the backend
 * expects; after a successful upload the editor is NOT auto-opened for
 * media captures — the new note just appears in the list the same way as
 * QuickCreate does.
 */
export default function MobileFab({ onCreate, onOpenEditor }) {
  const [open, setOpen] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [recording, setRecording] = useState(false);
  const imageInputRef = useRef(null);    // Camera (photo) — capture=environment
  const galleryInputRef = useRef(null);  // Pick existing image from gallery
  const videoInputRef = useRef(null);    // Camera (video) — capture=user/env
  const docInputRef = useRef(null);      // Any file (PDF, Word, text, …)

  const close = () => setOpen(false);

  const createEmptyNote = async (checklist = false) => {
    close();
    try {
      const created = await onCreate({
        title: "",
        content: "",
        is_checklist: checklist,
        checklist_items: checklist ? [{ id: `tmp-${Date.now()}`, text: "", checked: false }] : [],
      });
      if (created && onOpenEditor) onOpenEditor(created);
    } catch (err) {
      toast.error("Could not create note");
      console.warn("[fab] create failed:", err);
    }
  };

  const onImagePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    close();
    if (!file) return;
    try {
      const { path } = await uploadImage(file);
      // Create note with the image attached, THEN open the editor so the
      // user can add a title / description right away (per product spec).
      const created = await onCreate({ title: "", content: "", images: [path] });
      if (created && onOpenEditor) onOpenEditor(created);
    } catch (err) {
      toast.error("Could not upload image");
      console.warn("[fab] image upload failed:", err);
    }
  };

  // Video capture flow. The browser's upload API doesn't know "video" and
  // "image" separately — we just send to /api/upload-doc with the actual
  // video/* mime type and store it as a note attachment. The editor opens
  // so the user can add context.
  const onVideoPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    close();
    if (!file) return;
    try {
      const data = await uploadDocument(file);
      const created = await onCreate({
        title: "",
        content: "",
        attachments: [{
          path: data.path,
          filename: data.filename || file.name,
          size: data.size || file.size,
          content_type: data.content_type || file.type || "video/mp4",
        }],
      });
      if (created && onOpenEditor) onOpenEditor(created);
    } catch (err) {
      toast.error("Could not upload video");
      console.warn("[fab] video upload failed:", err);
    }
  };

  const onDocPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    close();
    if (!file) return;
    try {
      const data = await uploadDocument(file);
      const created = await onCreate({ title: "", content: "", attachments: [{
        path: data.path, filename: data.filename, size: data.size, content_type: data.content_type,
      }] });
      if (created && onOpenEditor) onOpenEditor(created);
    } catch (err) {
      toast.error("Could not upload file");
      console.warn("[fab] doc upload failed:", err);
    }
  };

  const onDrawingSaved = async (path) => {
    try {
      const created = await onCreate({ title: "Drawing", content: "", images: [path] });
      if (created && onOpenEditor) onOpenEditor(created);
    } catch (err) {
      toast.error("Could not save drawing");
      console.warn("[fab] drawing create failed:", err);
    }
  };

  const onAudioSaved = async (attachment) => {
    try {
      const created = await onCreate({
        title: "Voice note",
        content: "",
        attachments: [{
          path: attachment.path,
          filename: attachment.filename,
          content_type: attachment.content_type,
          size: attachment.size,
        }],
      });
      // Audio: open editor so the user can add a title / caption.
      if (created && onOpenEditor) onOpenEditor(created);
    } catch (err) {
      toast.error("Could not save voice note");
      console.warn("[fab] audio create failed:", err);
    }
  };

  // Listed nearest-to-button first: Note, then Audio, then the rest.
  const actions = [
    { key: "note",     icon: StickyNote,  label: "Note",      onClick: () => createEmptyNote(false), color: "bg-amber-500" },
    { key: "audio",    icon: Mic,         label: "Audio",     onClick: () => { close(); setRecording(true); }, color: "bg-indigo-500" },
    { key: "list",     icon: CheckSquare, label: "Checklist", onClick: () => createEmptyNote(true),  color: "bg-emerald-500" },
    { key: "camera",   icon: Camera,      label: "Photo",     onClick: () => { imageInputRef.current?.click(); }, color: "bg-sky-500" },
    { key: "video",    icon: Video,       label: "Video",     onClick: () => { videoInputRef.current?.click(); }, color: "bg-fuchsia-500" },
    { key: "image",    icon: ImageIcon,   label: "Gallery",   onClick: () => { galleryInputRef.current?.click(); }, color: "bg-teal-500" },
    { key: "doc",      icon: FileText,    label: "File",      onClick: () => { docInputRef.current?.click(); }, color: "bg-stone-500" },
    { key: "draw",     icon: Pencil,      label: "Drawing",   onClick: () => { close(); setDrawing(true); }, color: "bg-rose-500" },
  ].reverse();

  return (
    <>
      {/* Only visible on phones (<md); desktop keeps the QuickCreate bar. */}
      <div className="md:hidden fixed bottom-5 right-5 z-40 flex flex-col items-end gap-2 pointer-events-none">
        <AnimatePresence>
          {open && actions.map((a, i) => (
            <motion.button
              key={a.key}
              type="button"
              initial={{ opacity: 0, y: 20, scale: 0.6 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.6 }}
              transition={{ duration: 0.15, delay: i * 0.03 }}
              onClick={a.onClick}
              className="pointer-events-auto flex items-center gap-2 pr-1 pl-3"
              data-testid={`fab-action-${a.key}`}
            >
              <span className="text-xs font-medium bg-stone-900/85 dark:bg-stone-100/90 text-white dark:text-stone-900 rounded-full px-2.5 py-1 shadow">
                {a.label}
              </span>
              <span className={cn("h-11 w-11 rounded-full flex items-center justify-center text-white shadow-lg", a.color)}>
                <a.icon className="h-5 w-5" />
              </span>
            </motion.button>
          ))}
        </AnimatePresence>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "pointer-events-auto h-14 w-14 rounded-full flex items-center justify-center text-white shadow-xl transition-all duration-200",
            open ? "bg-stone-900 dark:bg-stone-100 dark:text-stone-900 scale-110" : "bg-gradient-to-br from-amber-500 to-rose-500"
          )}
          data-testid="fab-toggle"
          aria-label={open ? "Close menu" : "Create"}
        >
          {open ? <X className="h-7 w-7" strokeWidth={2.5} /> : <Plus className="h-7 w-7" strokeWidth={2.5} />}
        </button>
      </div>

      {/* Hidden file inputs. `capture="environment"` tells the phone
          browser to launch the rear camera. The "Gallery" input omits
          capture so it picks from the camera roll. The "File" input has
          no accept restriction so PDFs, Word docs, text, zips all go. */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onImagePicked}
        data-testid="fab-camera-input"
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        capture="environment"
        className="hidden"
        onChange={onVideoPicked}
        data-testid="fab-video-input"
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onImagePicked}
        data-testid="fab-image-input"
      />
      <input
        ref={docInputRef}
        type="file"
        accept="application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.md,.rtf,.zip,application/*,text/*"
        className="hidden"
        onChange={onDocPicked}
        data-testid="fab-doc-input"
      />

      <DrawingPad open={drawing} onOpenChange={setDrawing} onSave={onDrawingSaved} />
      <AudioRecorder open={recording} onOpenChange={setRecording} onSave={onAudioSaved} />
    </>
  );
}
